import type { CSSProperties } from 'react';

import type { ProfileCustomization, PlatformStatistics } from '@perplexicon/api-client';
import type { AvatarConfig } from '@perplexicon/validation';

import { HeroFrame } from '../../../components/index.js';
import { resolveCosmeticAsset } from '../profile-assets.js';
import { AvatarDisplay } from './AvatarDisplay.js';
import { HeroThemeBackdrop } from './HeroThemeBackdrop.js';
import { OverallStats } from './OverallStats.js';
import { ProfileIdentity } from './ProfileIdentity.js';
import { ProfileSideDecoration } from './ProfileSideDecoration.js';

/**
 * The unified profile hero (APP_SPEC §13, §13.2): one large parchment/brass
 * region carrying the framed avatar, the identity + selected title, the
 * overall stats, and the decorative side image — replacing the old separate
 * profile-header and Statistics cards. It reads as one illustrated RPG
 * profile plate: identity anchored left, overall stats to its right, theme
 * art carried behind and along the far right edge.
 *
 * Three independently-selected cosmetics feed it:
 *   - `avatarFrameId`     -> the frame layered over the avatar
 *   - `heroBackgroundId`  -> the full-bleed background image
 *   - `sideDecorationId`  -> the far-right decoration
 * Each resolves to a neutral placeholder when unset. The theme art is
 * *atmosphere*, not subject matter: the hero should read as an environment
 * seen *through* parchment, not as transparent UI sitting on a photograph. So
 * it is built as genuine layers, bottom to top:
 *
 *   theme artwork (its own element, desaturated and flattened in contrast)
 *     →  warm parchment veil, heaviest over the identity end
 *     →  the parchment texture the rest of the app's surfaces use
 *     →  the side decoration
 *     →  fully opaque profile UI
 *
 * The wash lives on that dedicated artwork layer, never as an `opacity` on the
 * hero itself: fading the container would fade the name, the joined date, the
 * title and the stat plaques with it. For the same reason the art's
 * `filter` is confined to the layer — it is the only element the filter may
 * touch, so no foreground text ever inherits a contrast reduction.
 */
export interface ProfileHeroProps {
  userId: string;
  username: string;
  joinedAt: string;
  avatar: AvatarConfig | null;
  statistics: PlatformStatistics;
  customization: ProfileCustomization;
}

export function ProfileHero({
  userId,
  username,
  joinedAt,
  avatar,
  statistics,
  customization,
}: ProfileHeroProps) {
  const backgroundUrl = customization.heroBackgroundId
    ? resolveCosmeticAsset('heroBackground', customization.heroBackgroundId)
    : null;

  // With no decoration equipped there is nothing to reserve room for, so the
  // gutter collapses to the hero's ordinary inset and the Overall bay runs
  // out to it — rather than the totals stopping short of a 118px strip of
  // empty parchment. Inline so it wins at every breakpoint over the
  // decoration widths declared in `className` below.
  const noDecorationGutter = customization.sideDecorationId
    ? undefined
    : ({ '--px-hero-decoration-gutter': '1.5rem' } as CSSProperties);

  return (
    <HeroFrame
      as="section"
      aria-label={`${username} profile`}
      // No `overflow-hidden` here: the side decoration is allowed to bleed a
      // little past the top edge like a real hanging banner.
      // `--px-hero-decoration-gutter` is the horizontal space the hero gives
      // up to the side decoration. It is declared once, here on the hero, and
      // read by *both* consumers — the grid below reserves it as right
      // padding, `ProfileSideDecoration` caps its own size against it — so
      // the reserved space and the art occupying it cannot drift apart.
      className="rounded-lg bg-parchment text-textPrimary shadow-3 desktop:[--px-hero-decoration-gutter:118px] wide:[--px-hero-decoration-gutter:128px]"
      style={noDecorationGutter}
    >
      {/* The theme artwork, as its own inert layer rather than as the panel's
          `background` — extracted into `HeroThemeBackdrop` so the Settings
          preview can share the exact same material. */}
      <HeroThemeBackdrop backgroundUrl={backgroundUrl} />

      {/* Decorative art on the far right (desktop only): base anchored to the
          hero's bottom-right corner, top allowed to overhang the hero edge.
          Absolutely positioned so it never grows the hero or displaces the
          identity / Overall content — the grid below just reserves right
          padding for it. */}
      <ProfileSideDecoration decorationId={customization.sideDecorationId} />

      {/* Desktop is one compact row — avatar | identity | Overall stats —
          vertically centred. Below desktop the Overall block drops to its own
          full-width row beneath the identity and the side art is hidden. */}
      {/* The identity cluster (avatar + name + joined + title) and the Overall
          bay are laid out as one grid, so tightening the hero is a matter of
          re-allocating this grid's own padding and column gaps — nothing is
          nudged into place with a transform or a negative margin.

          The Overall track carries a floor rather than being a bare
          `minmax(0,1fr)`. The two `auto` tracks beside it are sized to their
          content first, so in a container narrower than a page — the hero
          preview inside Settings is one — they took everything and left the
          totals a ~20px column with five plaques stacked on top of each
          other. With a floor the auto tracks give way instead: the joined
          date wraps and the plaques stay readable. `min(…,100%)` keeps the
          floor from itself overflowing a container narrower than the floor. */}
      {/* On desktop the hero's left inset and its vertical inset are the same
          value (`p-7` = 28px), which is the whole of the avatar's placement
          rule: `AvatarDisplay` reserves a square box centred on the frame's
          *ring*, and the row centres that box vertically, so equal insets put
          the ring's centre the same distance from the hero's left, top and
          bottom inner edges. Nothing here measures the frame PNG's bounds —
          an asset whose laurels hang further one way than the other changes
          only how much empty space the box reserves around that centre. */}
      <div className="relative z-[1] grid gap-x-5 gap-y-7 p-5 tablet:grid-cols-[auto_1fr] tablet:gap-x-6 tablet:p-6 desktop:grid-cols-[auto_auto_minmax(min(15rem,100%),1fr)] desktop:items-center desktop:gap-x-6 desktop:p-7 desktop:pr-[var(--px-hero-decoration-gutter)]">
        <div className="justify-self-center tablet:justify-self-start">
          <AvatarDisplay
            userId={userId}
            username={username}
            avatar={avatar}
            frameId={customization.avatarFrameId}
            size={176}
          />
        </div>

        <div className="min-w-0 self-center">
          <ProfileIdentity
            username={username}
            joinedAt={joinedAt}
            titleId={customization.selectedTitleId}
          />
        </div>

        {/* A ruled divider splits the hero into its two bays — who this is,
            and what they have done. Desktop only: below that the Overall
            block already sits on its own row, where a vertical rule would
            have nothing to separate. */}
        <div className="min-w-0 tablet:col-span-2 desktop:col-span-1 desktop:col-start-3 desktop:border-l desktop:border-borderStrong/35 desktop:pl-7 wide:pl-9">
          <OverallStats statistics={statistics} />
        </div>
      </div>
    </HeroFrame>
  );
}
